import { Link, useParams } from 'react-router-dom';
import { useState } from 'react';
import { getRepo, type Article } from '../../lib/repo';
import { useAsync } from '../../lib/hooks';
import { absoluteDate, readingTime, relativeTime } from '../../lib/format';
import { Seo, siteUrl } from '../../components/Seo';
import { ArticleCard } from '../../components/ArticleCard';
import { Spinner, useToast } from '../../components/ui';
import { NotFoundPage } from './NotFoundPage';

/** Share targets. The URL is ALWAYS the canonical article URL built from the slug. */
function shareTargets(url: string, title: string) {
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(title);
  return [
    { label: 'WhatsApp', href: `https://wa.me/?text=${t}%20${u}` },
    { label: 'X', href: `https://twitter.com/intent/tweet?text=${t}&url=${u}` },
    { label: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${u}` },
    { label: 'LinkedIn', href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}` },
    { label: 'Telegram', href: `https://t.me/share/url?url=${u}&text=${t}` },
    { label: 'Email', href: `mailto:?subject=${t}&body=${u}` },
  ];
}

function ShareBar({ article }: { article: Article }) {
  const url = `${siteUrl().replace(/\/$/, '')}/article/${article.slug}`;
  const toast = useToast();
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast('Link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast('Could not copy — select the URL manually', 'error');
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2 border-y border-border py-3">
      <span className="mr-1 text-[11px] font-semibold uppercase tracking-widest text-muted">Share</span>
      {shareTargets(url, article.title).map((s) => (
        <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer"
           className="rounded-full border border-border px-3 py-1 text-xs hover:border-primary hover:text-primary">
          {s.label}
        </a>
      ))}
      <button type="button" onClick={copy} className="rounded-full border border-border px-3 py-1 text-xs hover:border-primary hover:text-primary">
        {copied ? 'Copied!' : 'Copy link'}
      </button>
      {/* The exact shared URL, shown so it can be verified at a glance. */}
      <code className="ml-auto max-w-full truncate rounded bg-background px-2 py-1 text-[11px] text-muted" title={url}>{url}</code>
    </div>
  );
}

export function ArticlePage() {
  const { slug = '' } = useParams();
  const { data, loading, error } = useAsync<Article | null>(() => getRepo().getPublicArticleBySlug(slug), [slug]);
  const { data: related } = useAsync<Article[]>(
    () => getRepo().listPublicArticles({ categoryId: data?.categoryId, limit: 4 }),
    [data?.categoryId, slug],
  );

  if (loading) return <Spinner label="Loading article…" />;
  if (error) return <div className="mx-auto max-w-3xl px-4 py-16 text-danger">{error}</div>;
  // A slug with no published article is a genuine 404 — never a blank page.
  if (!data) return <NotFoundPage />;

  const a = data;
  const byline = a.author?.name ?? 'The Story Line';
  const keepReading = (related ?? []).filter((r) => r.id !== a.id).slice(0, 3);

  return (
    <>
      <Seo name="article" article={a} category={a.category} />
      <div className="mx-auto max-w-3xl px-4 py-10">
        <Link to="/" className="text-xs uppercase tracking-widest text-muted hover:text-primary">← Back to home</Link>
        <p className="mt-6 text-xs uppercase tracking-widest text-primary">
          {a.category ? <Link to={`/category/${a.category.slug}`}>{a.category.name}</Link> : null}
        </p>
        <h1 className="mt-2 font-display text-4xl font-semibold leading-tight">{a.title}</h1>
        {a.dek ? <p className="mt-4 text-lg text-muted">{a.dek}</p> : null}

        <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted">
          <span>By <strong className="font-medium text-foreground">{byline}</strong></span>
          <time dateTime={a.publishedAt ?? undefined}>{absoluteDate(a.publishedAt)}</time>
          <span>{relativeTime(a.publishedAt)}</span>
          <span>{readingTime(a.body)}</span>
          {a.viewCount > 0 ? <span>{a.viewCount.toLocaleString()} views</span> : null}
        </div>

        {a.coverImageUrl ? (
          <img src={a.coverImageUrl} alt={a.title} className="mt-6 w-full rounded-xl object-cover" width={1200} height={700} />
        ) : null}

        <div className="prose-article mt-8">
          {a.body.split('\n').filter(Boolean).map((para, i) => <p key={i}>{para}</p>)}
        </div>

        {a.tags.length ? (
          <ul className="mt-8 flex flex-wrap gap-2">
            {a.tags.map((t) => (
              <li key={t} className="rounded-full border border-border px-3 py-1 text-xs text-muted">{t}</li>
            ))}
          </ul>
        ) : null}

        <div className="mt-8"><ShareBar article={a} /></div>

        {keepReading.length ? (
          <section className="mt-12" aria-labelledby="keep-reading">
            <h2 id="keep-reading" className="mb-6 border-b border-border pb-2 font-display text-xl font-semibold">Keep reading</h2>
            <div className="grid gap-8 sm:grid-cols-3">
              {keepReading.map((r) => <ArticleCard key={r.id} article={r} />)}
            </div>
          </section>
        ) : null}
      </div>
    </>
  );
}
